import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  AlertCircle,
  CheckCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  runTransaction,
  getDoc,
  increment,
  doc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const VisualSearchTask = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game Modes: "intro", "tutorial", "play", "results"
  const [gameMode, setGameMode] = useState("intro");
  const [difficulty, setDifficulty] = useState(1); // Levels: 1 (easiest) to 3 (hardest)
  const [gameCompleted, setGameCompleted] = useState(false);

  // Score and metrics
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);
  const [hitCount, setHitCount] = useState(0);
  const [commissionErrors, setCommissionErrors] = useState(0);
  const [missedTargets, setMissedTargets] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [hitStreak, setHitStreak] = useState(0);

  // Current trial/grid state
  const [currentGrid, setCurrentGrid] = useState([]);
  const [currentResponseRecorded, setCurrentResponseRecorded] = useState(false);
  const [stimulusDisplayTime, setStimulusDisplayTime] = useState(null);

  // Game Timer settings
  const GAME_DURATION = 60; // seconds
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const trialTimeoutRef = useRef(null);
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Stimulus display interval (time per grid/trial, in milliseconds)
  const STIMULUS_INTERVAL = 2500; // 2.5 seconds per grid

  //new
  // New color scheme for the category:
  const primaryColor = "from-amber-500 to-orange-600";
  const primaryColorSolid = "bg-amber-500";
  const secondaryColor = "text-amber-300";
  const tertiaryColor = "bg-amber-500/10";

  // Tutorial state
  const [tutorialStep, setTutorialStep] = useState(0);

  // --- Fetch High Scores ---
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // Reference the single leaderboard document for the game "visual-search"
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "visual-search"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const scores =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };
    fetchHighScores();
  }, [organizationId, difficulty]);

  // --- Timer Effect ---
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // --- Generate Grid for Current Trial ---
  // Grid dimensions (rows = columns) depend on difficulty:
  // Level 1: 3x3, Level 2: 4x4, Level 3: 5x5
  const generateGrid = useCallback(() => {
    const gridSize = difficulty + 2;
    const totalCells = gridSize * gridSize;
    // Set target probability (target becomes less frequent as difficulty increases)
    let targetProbability;
    if (difficulty === 1) targetProbability = 0.8;
    else if (difficulty === 2) targetProbability = 0.6;
    else targetProbability = 0.4;

    const hasTarget = Math.random() < targetProbability;
    const targetIndex = hasTarget
      ? Math.floor(Math.random() * totalCells)
      : null;

    const grid = [];
    for (let i = 0; i < totalCells; i++) {
      grid.push({ id: i, isTarget: hasTarget && i === targetIndex });
    }
    return grid;
  }, [difficulty]);

  // --- Show Next Trial ---
  const showNextTrial = () => {
    // Check previous trial: if target was present but no response was made, count as missed target.
    if (currentGrid.length > 0 && !currentResponseRecorded) {
      const previousHasTarget = currentGrid.some((cell) => cell.isTarget);
      if (previousHasTarget) {
        setMissedTargets((prev) => prev + 1);
        setScore((prev) => Math.max(0, prev - 3)); // NEW: missed target penalty
        setHitStreak(0); // Reset streak
      }
    }

    // End trial if game is no longer playing
    if (!isPlayingRef.current) return;

    // Generate a new grid and set it as current trial
    const grid = generateGrid();
    setCurrentGrid(grid);
    setCurrentResponseRecorded(false);
    setStimulusDisplayTime(Date.now());

    // Schedule next trial
    trialTimeoutRef.current = setTimeout(() => {
      showNextTrial();
    }, STIMULUS_INTERVAL);
  };

  // --- Start Game ---
  const startGame = () => {
    console.log("[DEBUG] Starting Visual Search Game.");
    setScore(0);
    setHitCount(0);
    setHitStreak(0);
    setCommissionErrors(0);
    setMissedTargets(0);
    setReactionTimes([]);
    setTimeLeft(GAME_DURATION);
    setIsPlaying(true);

    // Clear any previous trial timeouts
    if (trialTimeoutRef.current) clearTimeout(trialTimeoutRef.current);

    // Begin with the first trial almost immediately
    setTimeout(() => {
      showNextTrial();
    }, 100);
  };

  // --- End Game ---
  const endGame = () => {
    console.log("[DEBUG] Ending game. Final score:", score);
    console.log("[DEBUG] timeLeft before ending:", timeLeft);
    clearInterval(timerRef.current);
    clearTimeout(trialTimeoutRef.current);
    setIsPlaying(false);
    setGameMode("results");
    setGameCompleted(timeLeft <= 1); // Always mark the game as complete
  };

  //scoring done here
  const handleCellClick = (cell) => {
    if (!isPlayingRef.current || currentResponseRecorded) return;

    const responseTime = Date.now() - stimulusDisplayTime;
    setCurrentResponseRecorded(true);

    if (cell.isTarget) {
      // --- Base points for correct hit ---
      let basePoints = 20;

      // --- Reaction time bonus ---
      let rtBonus = 0;
      if (responseTime < 600) rtBonus = 10;
      else if (responseTime < 1000) rtBonus = 5;

      // --- Combo bonus ---
      const newStreak = hitStreak + 1;
      let comboBonus = 0;
      if (newStreak % 10 === 0) comboBonus = 25;
      else if (newStreak % 5 === 0) comboBonus = 10;
      else if (newStreak % 3 === 0) comboBonus = 5;

      const totalPoints = basePoints + rtBonus + comboBonus;

      setScore((prev) => prev + totalPoints);
      setHitCount((prev) => prev + 1);
      setHitStreak(newStreak);
      setReactionTimes((prev) => [...prev, responseTime]);
    } else {
      // --- Commission error ---
      setCommissionErrors((prev) => prev + 1);
      setScore((prev) => Math.max(0, prev - 10));
      setHitStreak(0); // reset streak on error
    }
  };

  // --- Calculate Reaction Time Variability ---
  const calculateRTVariability = () => {
    if (reactionTimes.length < 2) return null;
    const mean =
      reactionTimes.reduce((acc, rt) => acc + rt, 0) / reactionTimes.length;
    const variance =
      reactionTimes.reduce((acc, rt) => acc + Math.pow(rt - mean, 2), 0) /
      (reactionTimes.length - 1);
    return Math.round(Math.sqrt(variance));
  };

  const compositeComparator = (a, b) => {
    // First, sort by overall score (descending)
    if (a.score !== b.score) return b.score - a.score;

    // Next, sort by hit count (descending)
    if (a.hitCount !== b.hitCount) return b.hitCount - a.hitCount;

    // Then, sort by commission errors (ascending)
    if ((a.commissionErrors || 0) !== (b.commissionErrors || 0))
      return a.commissionErrors - b.commissionErrors;

    // Finally, sort by average reaction time (ascending)
    return a.averageReactionTime - b.averageReactionTime;
  };

  // --- Save Score to Firebase ---
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // Resolve userName
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      const gameData = {
        gameType: "visual-search",
        gameId: "visual-search",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        hitCount,
        commissionErrors,
        missedTargets,
        averageReactionTime:
          reactionTimes.length > 0
            ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
            : 0,
        reactionTimeVariability:
          reactionTimes.length > 1 ? calculateRTVariability() : null,
        gameLength: GAME_DURATION - timeLeft,
        timestamp: new Date(),
      };

      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        gameData.gameId
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };
        const difficultyKey = gameData.difficulty.toString();
        let currentLeaderboard =
          leaderboardData.leaderboards[difficultyKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[difficultyKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, { gameScore: increment(score) });
      });

      // Save game data to game history
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );
      await setDoc(gameHistoryRef, gameData);

      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  // --- Advance Tutorial ---
  const advanceTutorial = () => {
    if (tutorialStep < 2) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  // Determine grid dimensions based on difficulty
  const gridSize = difficulty + 2;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-green-300">
              Visual Seeker
            </h1>
            <div className={`p-2 ${primaryColorSolid} rounded-full`}>
              <Brain className="text-green-400" size={20} />
            </div>
          </div>
          <button
            onClick={() => console.log("[DEBUG] Info button clicked.")}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 bg-gradient-to-br ${primaryColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Visual Seeker
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Find the unique target (🔶) hidden among distractors (🔷) as
                  quickly as possible.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className={`p-3 ${primaryColorSolid} rounded-full mb-2`}>
                    <AlertCircle size={24} className="text-green-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">Visual Search</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Improve your ability to detect subtle differences in a
                    cluttered scene.
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className={`p-3 ${primaryColorSolid} rounded-full mb-2`}>
                    <Clock size={24} className="text-green-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">60 Seconds</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Sharpen your focus during a minute-long challenge.
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className={`p-3 ${primaryColorSolid} rounded-full mb-2`}>
                    <Trophy size={24} className="text-green-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">High Scores</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Compete and track your progress over time.
                  </p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((level) => (
                      <button
                        key={level}
                        onClick={() => {
                          console.log("[DEBUG] Difficulty changed to:", level);
                          setDifficulty(level);
                        }}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          difficulty === level
                            ? `bg-gradient-to-r ${primaryColor} text-white`
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        Level {level}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      console.log("[DEBUG] Tutorial button clicked.");
                      setGameMode("tutorial");
                    }}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>
                  <button
                    onClick={() => {
                      console.log("[DEBUG] Play Now button clicked.");
                      setGameMode("play");
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Play Now
                  </button>
                </div>
              </div>

              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-green-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Avg. RT
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            RT Variability
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((score, index) => {
                          let formattedName = score.userName;
                          if (score.userName && score.userName.includes(" ")) {
                            const nameParts = score.userName.split(" ");
                            formattedName = `${nameParts[0]} ${nameParts[1][0]}.`;
                          }
                          return (
                            <tr
                              key={score.id}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300">
                                Level {score.difficulty}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {score.averageReactionTime
                                  ? Math.round(score.averageReactionTime) +
                                    " ms"
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {score.reactionTimeVariability
                                  ? Math.round(score.reactionTimeVariability) +
                                    " ms"
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-right text-green-300 font-medium">
                                {score.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4">
                Tutorial: Visual Search Task
              </h2>
              {tutorialStep === 0 && (
                <div className="space-y-4">
                  <p className="text-slate-300">
                    In each trial, you will see a grid filled with icons.
                  </p>
                  <p className="text-slate-300">
                    Most cells show a distractor (🔷). Sometimes, one cell will
                    display the target (🔶).
                  </p>
                </div>
              )}
              {tutorialStep === 1 && (
                <div className="space-y-4">
                  <p className="text-slate-300">
                    Your goal is to quickly locate and click the target when it
                    appears.
                  </p>
                  <p className="text-slate-300">
                    If you click on a distractor, it counts as a commission
                    error.
                  </p>
                </div>
              )}
              {tutorialStep === 2 && (
                <div className="space-y-4">
                  <p className="text-slate-300">
                    If the target is present and you miss clicking it before the
                    grid changes, it counts as a missed target.
                  </p>
                  <p className="text-slate-300">
                    Try to be both fast and accurate as you search through the
                    grid.
                  </p>
                </div>
              )}
              <div className="flex justify-end mt-6">
                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 2 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Gameplay Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-600/20 px-4 py-2 rounded-lg">
                  <span className="text-green-300 font-medium">
                    Difficulty: Level {difficulty}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                  <div className="bg-gradient-to-r from-amber-500/20 to-orange-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Grid Display */}
                <div className="flex-1 flex justify-center items-center">
                  {currentGrid && currentGrid.length > 0 && (
                    <div
                      className="grid gap-2"
                      style={{
                        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                      }}
                    >
                      {currentGrid.map((cell) => (
                        <button
                          key={cell.id}
                          onClick={() => handleCellClick(cell)}
                          className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center text-2xl font-bold bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
                        >
                          {cell.isTarget ? "🔶" : "🔷"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Controls */}
                <div className="flex-1 space-y-6">
                  <button
                    onClick={() => {
                      console.log("[DEBUG] End Game button clicked.");
                      if (
                        confirm("Are you sure you want to end the game early?")
                      ) {
                        endGame();
                      }
                    }}
                    className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                  >
                    End Game
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 bg-gradient-to-br ${primaryColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You've finished the visual search challenge.
                </p>
              </div>
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="text-green-400 font-medium">Hits</span>:
                  Correct clicks on the target.
                  <br />
                  <span className="text-red-400 font-medium">
                    Commission Errors
                  </span>
                  : Clicking on distractors.
                  <br />
                  <span className="text-amber-400 font-medium">
                    Missed Targets
                  </span>
                  : Failing to click the target when present.
                  <br />
                  <span className="text-amber-400 font-medium">
                    Reaction Time Variability
                  </span>
                  : Consistency in your response time.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-green-400" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-green-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Hits</span>
                      <span className="text-green-400">{hitCount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Commission Errors</span>
                      <span className="text-red-400">{commissionErrors}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Missed Targets</span>
                      <span className="text-amber-400">{missedTargets}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Reaction Time</span>
                      <span className="text-white">
                        {reactionTimes.length > 0
                          ? `${Math.round(
                              reactionTimes.reduce((a, b) => a + b, 0) /
                                reactionTimes.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Reaction Time Variability
                      </span>
                      <span className="text-white">
                        {calculateRTVariability()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-green-400" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {hitCount + commissionErrors > 0
                            ? `${Math.round(
                                (hitCount / (hitCount + commissionErrors)) * 100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-600"
                          style={{
                            width:
                              hitCount + commissionErrors > 0
                                ? `${
                                    (hitCount / (hitCount + commissionErrors)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div
                            className={`p-1 ${primaryColorSolid} rounded-full mt-0.5`}
                          >
                            <Brain size={14} className="text-green-400" />
                          </div>
                          <span className="text-slate-300">
                            Enhanced visual discrimination
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div
                            className={`p-1 ${primaryColorSolid} rounded-full mt-0.5`}
                          >
                            <Brain size={14} className="text-green-400" />
                          </div>
                          <span className="text-slate-300">
                            Improved focused attention
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    console.log("[DEBUG] Back to Menu clicked.");
                    setGameMode("intro");
                  }}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>
                {gameCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}
                <button
                  onClick={() => {
                    console.log("[DEBUG] Play Again clicked.");
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Trophy size={18} />
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualSearchTask;
